import CalendarIcon from "@atlaskit/icon/core/calendar";
import ClockIcon from "@atlaskit/icon/core/clock";
import {DatePickerInput} from "@mantine/dates";
import {Select} from "@mantine/core";
import {useForm} from "@mantine/form";
import {useMutation} from "@tanstack/react-query";
import {token} from "@atlaskit/tokens";
import {useEffect, useMemo} from "react";
import {RowEngagementModal} from "~/components/ottopilotDataViewer/RowEngagementModal";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import type {ScheduleCallStrategy} from "~/domainModels/scheduleCall/scheduleCallStrategy";
import {submitScheduleCall} from "~/domainModels/scheduleCall/submitScheduleCall";
import {
    disablePastScheduleCallTimeSlots,
    isScheduleCallTimeSlotInThePast,
} from "~/domainModels/scheduleCall/scheduleCallShared";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeButtonGroup} from "@vesta/ananke/components/anankeButtonGroup";
import {AnankeField} from "@vesta/ananke/components/anankeField";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {showErrorToast} from "~/utilities/utilities";

function asValidDateOrNull(value: unknown): Date | null {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) === true ? null : value;
    }
    if (typeof value === "string" || typeof value === "number") {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) === true ? null : parsed;
    }
    return null;
}

function formFieldError(error: unknown): string | undefined {
    return typeof error === "string" ? error : undefined;
}

const scheduleCallFieldStyles = {
    input: {
        minHeight: "40px",
        borderRadius: "3px",
        border: `1px solid ${token("color.border.input")}`,
        background: token("color.background.input"),
    },
};

export function ScheduleCallModal({
    opened,
    journey,
    strategy,
    onClose,
    onSuccess,
}: {
    opened: boolean;
    journey: EventContainerWithEvents | null;
    strategy: ScheduleCallStrategy;
    onClose: () => void;
    onSuccess?: () => void;
}) {
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {eventContainerTemplates, workspace} = useWorkspace();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const eventTemplates = useEventTemplates();
    const {showFlag} = useAnankeFlags();

    const callTemplate = useMemo(() => {
        return strategy.resolveCallEventTemplate(eventTemplates);
    }, [eventTemplates, strategy]);

    const form = useForm({
        initialValues: {
            callDate: null as Date | null,
            scheduledTimeSlot: "",
        },
        validate: {
            callDate: (value) => (value === null ? "Call date is required" : null),
            scheduledTimeSlot: (value) => (value.trim() === "" ? "Call time is required" : null),
        },
    });

    const callTimeSlotOptions = disablePastScheduleCallTimeSlots({
        options: strategy.buildCallTimeSlotOptions(),
        selectedDate: form.values.callDate,
        now: new Date(),
    });

    useEffect(() => {
        const scheduledTimeSlot = form.values.scheduledTimeSlot.trim();
        if (scheduledTimeSlot === "") {
            return;
        }
        if (
            isScheduleCallTimeSlotInThePast({
                selectedDate: form.values.callDate,
                timeSlot: scheduledTimeSlot,
                now: new Date(),
            }) === true
        ) {
            form.setFieldValue("scheduledTimeSlot", "");
        }
    }, [form, form.values.callDate, form.values.scheduledTimeSlot]);

    const minSchedulableCallDate = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const submitMutation = useMutation({
        mutationFn: async () => {
            if (journey === null) {
                throw new Error("Journey is not selected.");
            }
            if (callTemplate === undefined) {
                throw new Error("Call event template is not configured for this workspace.");
            }
            const journeyTemplate = eventContainerTemplates.find((template) => template.id === journey.templateId);
            if (journeyTemplate === undefined) {
                throw new Error(`Event Container template not found: ${journey.templateId}`);
            }
            const callDate = form.values.callDate;
            const scheduledTimeSlot = form.values.scheduledTimeSlot.trim();
            if (callDate === null || scheduledTimeSlot === "") {
                throw new Error("Call date and time are required.");
            }
            if (
                isScheduleCallTimeSlotInThePast({
                    selectedDate: callDate,
                    timeSlot: scheduledTimeSlot,
                    now: new Date(),
                }) === true
            ) {
                throw new Error("Call time cannot be in the past.");
            }

            return await submitScheduleCall({
                strategy,
                callTemplate,
                journeyTemplate,
                journey,
                callDate,
                timeSlot: scheduledTimeSlot,
                workspaceUserId: workspaceUser.userId,
                workspaceUserTemplateId: workspaceUser.templateId,
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl,
                workspaceId: workspace.id,
            });
        },
        onSuccess: () => {
            showFlag({
                title: "Call scheduled",
                description: "The call date and time were saved.",
                appearance: "success",
                isAutoDismiss: true,
            });
            form.reset();
            onClose();
            onSuccess?.();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : "Could not schedule call.";
            showFlag({title: "Schedule call failed", description: message, appearance: "error", isAutoDismiss: true});
            showErrorToast("Schedule call failed", message);
        },
    });

    const handleSave = () => {
        const {hasErrors} = form.validate();
        if (hasErrors) {
            return;
        }
        submitMutation.mutate();
    };

    const handleClose = () => {
        if (submitMutation.isPending) {
            return;
        }
        form.reset();
        onClose();
    };

    // Guard against losing an in-progress schedule on close / back / refresh.
    const isDirty = opened === true && (form.values.callDate !== null || form.values.scheduledTimeSlot.trim() !== "");
    const {requestClose, modal} = useUnsavedChangesGuard({isDirty, onDiscard: handleClose});

    const footerButtons = (
        <AnankeButtonGroup>
            <AnankeButton
                type="button"
                appearance="subtle"
                onClick={() => requestClose()}
            >
                Cancel
            </AnankeButton>
            <AnankeButton
                type="button"
                appearance="primary"
                isLoading={submitMutation.isPending}
                onClick={handleSave}
            >
                Save
            </AnankeButton>
        </AnankeButtonGroup>
    );

    return (
        <>
        {modal}
        <RowEngagementModal
            opened={opened}
            title="Schedule a call"
            onClose={() => requestClose()}
            footer={footerButtons}
            preventCloseWhileLoading={true}
            isLoading={submitMutation.isPending}
        >
            <div className="grid gap-y-ds-space-150">
                <AnankeField
                    id="schedule-call-date"
                    label="Call Date"
                    isRequired
                >
                    <DatePickerInput
                        id="schedule-call-date"
                        value={form.values.callDate}
                        onChange={(value) => form.setFieldValue("callDate", asValidDateOrNull(value))}
                        placeholder="Select"
                        minDate={minSchedulableCallDate}
                        valueFormat="DD MMM YYYY"
                        rightSection={<CalendarIcon label="" />}
                        styles={scheduleCallFieldStyles}
                        error={formFieldError(form.errors.callDate)}
                    />
                </AnankeField>
                <AnankeField
                    id="schedule-call-time"
                    label="Call time"
                    isRequired
                >
                    <Select
                        id="schedule-call-time"
                        data={callTimeSlotOptions}
                        value={form.values.scheduledTimeSlot === "" ? null : form.values.scheduledTimeSlot}
                        onChange={(value) => form.setFieldValue("scheduledTimeSlot", value ?? "")}
                        placeholder="Select"
                        searchable
                        nothingFoundMessage="No time slots"
                        rightSection={<ClockIcon label="" />}
                        styles={scheduleCallFieldStyles}
                        error={formFieldError(form.errors.scheduledTimeSlot)}
                    />
                </AnankeField>
            </div>
        </RowEngagementModal>
        </>
    );
}
